import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/router';
import { create } from 'zustand';
import { AGENT_BUILDER_ROUTE, INVESTIGATION_CHAT_PARAM, INVESTIGATION_ROUTE } from '@/config/constants';

// Routes that carry a live chat in `?chat=` (the Ask Percy flow → a created resource), mapped
// to the query param holding the resource id — the dock-release key is derived from it.
const CHAT_CARRYING_ROUTES: Record<string, string> = {
  [INVESTIGATION_ROUTE]: 'investigationId',
  [AGENT_BUILDER_ROUTE]: 'id'
};

// Which page visit the user released the dock on. Deliberately NOT persisted: a reload or a
// different resource re-docks the carried chat.
type DockReleaseState = {
  releasedFor: string | null;
  release: (key: string) => void;
};
const useReleaseStore = create<DockReleaseState>()(set => ({
  releasedFor: null,
  release: key => set({
    releasedFor: key
  })
}));

/**
 * Identifies the current carried-chat visit: route + concrete resource id, or null when the
 * route carries no chat. The chat id is deliberately excluded — the history dropdown rewrites
 * `?chat=` when the user switches chats, and that must not snap a released layout back into
 * the dock. Same reason it isn't the full URL: volatile params (`?artifact=`) would re-arm it.
 */
const useDockKey = () => {
  const {
    query,
    pathname
  } = useRouter();
  const idParam = CHAT_CARRYING_ROUTES[pathname];
  if (!idParam) {
    return null;
  }
  const carriedChat = query[INVESTIGATION_CHAT_PARAM];
  const resourceId = query[idParam];
  if (typeof carriedChat !== "string" || carriedChat.length === 0) {
    return null;
  }
  return typeof resourceId === "string" ? `${pathname}:${resourceId}` : null;
};

/**
 * Whether the current route should dock Percy as a sidebar: a chat is carried in the URL
 * (`?chat=` on an investigation or the agent builder, appended by the live Ask Percy →
 * resource flow), so the chat docks beside the resource it produced.
 *
 * The dock is an arrival default, not a lock: an explicit layout pick or collapse (via
 * `useInvestigationDockRelease`) releases it for the visit, after which the stored mode
 * drives the presentation again. Nothing here is persisted (so it never leaks off the
 * route) — mirrors the route-derived `lockedFullscreen`: read live at each consumer
 * (widget presentation, docked slot, slot width), never written to the layout store.
 */
const useInvestigationDock = () => {
  const key = useDockKey();
  const releasedFor = useReleaseStore(_temp);
  return key !== null && key !== releasedFor;
};

/** An explicit user layout/collapse action: un-docks the carried chat for this visit. */
export const useInvestigationDockRelease = () => {
  const $ = _c(3);
  const key = useDockKey();
  const release = useReleaseStore(_temp2);
  let t0;
  if ($[0] !== key || $[1] !== release) {
    t0 = () => {
      if (key !== null) {
        release(key);
      }
    };
    $[0] = key;
    $[1] = release;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

/** Test-only: clears the release so each case starts docked. */
export const resetInvestigationDockRelease = () => useReleaseStore.setState({
  releasedFor: null
});
export default useInvestigationDock;
function _temp(state) {
  return state.releasedFor;
}
function _temp2(state) {
  return state.release;
}